import {
    type FlexAlignType,
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    ToastAnchorStyleConstantType,
    ToastMotionConstantType,
    ToastDynamicStyleType,
    ToastMotionStep,
    ToastMotionType,
    ToastStackMode,
    ToastAlign,
    ToastSide
} from "./type";
import {
    TOAST_STACK
} from "./util";
import type {
    Mutable
} from "../../types";
import {
    type MotionIntentType,
    getMotionDuration,
    MOTION_EASINGS,
    MOTION_INTENTS
} from "../../helpers/motion";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    webStyle
} from "../../utils";

export const TOAST_ANCHOR_ALIGNMENTS: Record<ToastAlign, FlexAlignType> = {
    start: "flex-start",
    center: "center",
    end: "flex-end"
};

export const TOAST_SLOT_JUSTIFICATIONS: Record<ToastSide, ViewStyle["justifyContent"]> = {
    bottom: "flex-end",
    top: "flex-start"
};

export const TOAST_MOTION_INTENTS: Record<ToastMotionStep, MotionIntentType> = {
    close: "listRemove",
    open: "listInsert"
};

export const TOAST_SIDES: Array<ToastSide> = [
    "bottom",
    "top"
];

export const TOAST_STACK_MODES: Array<ToastStackMode> = [
    "deck",
    "list"
];

export const TOAST_ALIGNS: Array<ToastAlign> = [
    "center",
    "start",
    "end"
];

export const getToastMotion = ({
    isReduceMotionEnabled,
    closeAnimationDelay,
    openAnimationDelay,
    durations
}: ToastMotionConstantType): ToastMotionType => {
    const closeIntent = MOTION_INTENTS[TOAST_MOTION_INTENTS.close];

    const openIntent = MOTION_INTENTS[TOAST_MOTION_INTENTS.open];

    const closeDuration = closeAnimationDelay ?? closeIntent.duration;

    const openDuration = openAnimationDelay ?? openIntent.duration;

    return {
        closeEasing: MOTION_EASINGS[closeIntent.easing],
        openEasing: MOTION_EASINGS[openIntent.easing],
        closeFadeDuration: getMotionDuration({
            duration: closeDuration,
            isReduceMotionEnabled,
            motion: "fade",
            durations
        }),
        closeMoveDuration: getMotionDuration({
            duration: closeDuration,
            isReduceMotionEnabled,
            durations
        }),
        openFadeDuration: getMotionDuration({
            duration: openDuration,
            isReduceMotionEnabled,
            motion: "fade",
            durations
        }),
        openMoveDuration: getMotionDuration({
            duration: openDuration,
            isReduceMotionEnabled,
            durations
        })
    };
};

export const getToastAnchorStyle = ({
    stackMode = "deck",
    edgeOffset = 0,
    insets,
    spaces,
    align,
    side
}: ToastAnchorStyleConstantType): ViewStyle => {
    const margin = stackMode === "list" ? spaces.spacingSm : spaces.spacingLg;

    const horizontalOffset = Math.max(insets.left, insets.right) + margin;

    const anchorStyle: Mutable<ViewStyle> = {
        alignItems: TOAST_ANCHOR_ALIGNMENTS[align],
        start: horizontalOffset,
        end: horizontalOffset,
        gap: spaces.spacingSm
    };

    if(side === "top") {
        anchorStyle.top = insets.top + edgeOffset + margin;
    } else {
        anchorStyle.bottom = insets.bottom + edgeOffset + margin;
    }

    return anchorStyle;
};

export const getToastStackAnchorStyle = ({
    edgeOffset = 0,
    insets,
    spaces,
    align,
    side
}: ToastAnchorStyleConstantType): ViewStyle => {
    return {
        ...getToastAnchorStyle({
            edgeOffset,
            insets,
            spaces,
            align,
            side
        }),
        bottom: insets.bottom + spaces.spacingLg + (side === "bottom" ? edgeOffset : 0),
        top: insets.top + spaces.spacingLg + (side === "top" ? edgeOffset : 0)
    };
};

const stylesheet = StyleSheet.create({
    anchor: {
        pointerEvents: "box-none",
        flexDirection: "column",
        position: "absolute"
    },
    slot: {
        pointerEvents: "box-none",
        position: "absolute",
        bottom: 0,
        start: 0,
        end: 0,
        top: 0
    },
    cover: {
        pointerEvents: "none",
        position: "absolute"
    },
    filler: {
        position: "absolute",
        start: 0,
        end: 0
    },
    container: {
        maxWidth: "100%"
    }
});

export const useStyles = ({
    spreadBehaviour,
    isCompactWidth,
    isSwipeEnabled,
    isInteractive,
    shapeConfigs,
    isSwiping,
    stackMode,
    radiuses,
    borders,
    colors,
    spaces,
    zIndex,
    align,
    side,
    gap
}: ToastDynamicStyleType) => {
    const styles = {
        cover: {
            backgroundColor: colors.content.container.raised,
            ...getCornerStyle({
                radius: concentric({
                    outer: radiuses.surface,
                    role: radiuses.surface,
                    inset: borders.line
                }),
                shapeConfigs
            }),
            bottom: borders.line,
            start: borders.line,
            end: borders.line,
            top: borders.line
        } as Mutable<ViewStyle>,
        slot: {
            justifyContent: TOAST_SLOT_JUSTIFICATIONS[side],
            alignItems: TOAST_ANCHOR_ALIGNMENTS[align],
            zIndex
        } as Mutable<ViewStyle>,
        filler: {
            height: gap + TOAST_STACK.fillerOverlap
        } as Mutable<ViewStyle>,
        container: {
        } as Mutable<ViewStyle>
    };

    if(spreadBehaviour === "stretch") {
        styles.container.alignSelf = "stretch";
    }

    if(spreadBehaviour === "baseline") {
        styles.container.alignSelf = "baseline";
    }

    if(stackMode === "list" && isCompactWidth) {
        styles.container.marginHorizontal = spaces.spacingSm;

        styles.container.alignSelf = "stretch";
    }

    if(!isInteractive) {
        styles.container.pointerEvents = "none";
    }

    if(isSwipeEnabled) {
        Object.assign(styles.container, webStyle({
            touchAction: "none"
        }));
    }

    if(isSwiping) {
        Object.assign(styles.container, webStyle({
            userSelect: "none"
        }));
    }

    if(side === "top") {
        styles.filler.bottom = -(gap + TOAST_STACK.fillerOverlap);
    } else {
        styles.filler.top = -(gap + TOAST_STACK.fillerOverlap);
    }

    return styles;
};
export default stylesheet;
